import { useState } from 'react';
import { useLocale } from '@/contexts/LocaleContext';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Mail, Loader2, KeyRound } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';

interface ForgotPasswordDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export default function ForgotPasswordDialog({
  open,
  onOpenChange,
}: ForgotPasswordDialogProps) {
  const { locale } = useLocale();
  const { toast } = useToast();
  const isArabic = locale === 'ar';

  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/auth?mode=reset`,
      });

      if (error) throw error;

      setSent(true);
      toast({
        title: isArabic ? 'تم إرسال البريد' : 'Email Sent',
        description: isArabic 
          ? 'تحقق من بريدك الإلكتروني للحصول على رابط إعادة تعيين كلمة المرور'
          : 'Check your email for a password reset link',
      });
    } catch (error: any) {
      console.error('Reset password error:', error);
      // Don't reveal if email exists for security
      toast({
        title: isArabic ? 'تم إرسال البريد' : 'Email Sent',
        description: isArabic 
          ? 'إذا كان هذا البريد مسجلاً، سيصلك رابط إعادة تعيين كلمة المرور'
          : 'If this email is registered, you will receive a reset link',
      });
      setSent(true);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setEmail('');
    setSent(false);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <KeyRound className="w-5 h-5" />
            {isArabic ? 'نسيت كلمة المرور' : 'Forgot Password'}
          </DialogTitle>
          <DialogDescription>
            {isArabic 
              ? 'أدخل بريدك الإلكتروني وسنرسل لك رابطاً لإعادة تعيين كلمة المرور.'
              : 'Enter your email and we\'ll send you a link to reset your password.'}
          </DialogDescription>
        </DialogHeader>

        {sent ? (
          <div className="py-6 text-center">
            <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Mail className="w-8 h-8 text-primary" />
            </div>
            <h3 className="font-semibold mb-2">
              {isArabic ? 'تحقق من بريدك' : 'Check your email'}
            </h3>
            <p className="text-muted-foreground text-sm">
              {isArabic 
                ? 'أرسلنا رابط إعادة تعيين كلمة المرور إلى بريدك الإلكتروني.'
                : 'We\'ve sent a password reset link to your email.'}
            </p>
            <Button onClick={handleClose} className="mt-4 rounded-xl">
              {isArabic ? 'حسناً' : 'Got it'}
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="resetEmail">{isArabic ? 'البريد الإلكتروني' : 'Email'}</Label>
              <div className="relative">
                <Mail className="absolute start-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="resetEmail"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="ps-10 rounded-xl"
                  placeholder="example@email.com"
                  required
                />
              </div>
            </div>

            <DialogFooter className="gap-2 sm:gap-0">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                className="rounded-xl"
              >
                {isArabic ? 'إلغاء' : 'Cancel'}
              </Button>
              <Button type="submit" disabled={loading} className="rounded-xl">
                {loading && <Loader2 className="w-4 h-4 me-2 animate-spin" />}
                {isArabic ? 'إرسال الرابط' : 'Send Reset Link'}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}